import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Checkbox,
  MenuItem,
  FormControl,
  Menu,
  Box,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Collapse,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { useAppState } from 'store/Provider';
import { selectFilterAction, selectFilterRemoveAction } from 'store/actions/filterActions';

type City = {
  title: string;
  department_name: string;
};

type MockOptions = Record<string, City[]>;

interface CollapsibleSelectFilterProps {
  name: string;
  apiKeyname: string;
  label: string;
  disabled?: boolean;
  mockOptions: MockOptions;
  value: string[] | 'ALL';
  onChange: (selectedValues: string[] | 'ALL') => void;
  clearPerticularFilters: (apiKeyname: string) => void;
  setDisplaySelectedCountry: React.Dispatch<React.SetStateAction<string>>;
}

const CollapsibleCountryFilter: React.FC<CollapsibleSelectFilterProps> = ({
  name,
  apiKeyname,
  label,
  mockOptions,
  value,
  onChange,
  clearPerticularFilters,
  setDisplaySelectedCountry,
}) => {
  const { dispatch } = useAppState();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Store checked state with country-department key: "Country::Department"
  const [checkedDepartments, setCheckedDepartments] = useState<Record<string, boolean>>({});
  const [checkedCountries, setCheckedCountries] = useState<Record<string, boolean>>({});
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);

  // Process mockOptions to group by department with country context
  const processedOptions = useMemo(() => {
    const result: Record<string, { department: string; titles: string[]; key: string }[]> = {};

    Object.entries(mockOptions || {}).forEach(([country, cities]) => {
      const departmentMap = new Map<string, Set<string>>();

      cities.forEach((city) => {
        if (!departmentMap.has(city.department_name)) {
          departmentMap.set(city.department_name, new Set());
        }
        departmentMap.get(city.department_name)!.add(city.title);
      });

      result[country] = Array.from(departmentMap.entries()).map(([department, titlesSet]) => ({
        department,
        titles: Array.from(titlesSet),
        key: `${country}::${department}`, // Unique key combining country and department
      }));
    });

    return result;
  }, [mockOptions]);

  const allDepartmentKeys = useMemo(() => {
    const keys: string[] = [];
    Object.values(processedOptions).forEach((countryDepts) => {
      countryDepts.forEach((dept) => {
        keys.push(dept.key);
      });
    });
    return keys;
  }, [processedOptions]);

  const menuOpen = Boolean(anchorEl);

  useEffect(() => {
    const initialDepartments: Record<string, boolean> = {};
    const initialCountries: Record<string, boolean> = {};

    if (value === 'ALL') {
      allDepartmentKeys.forEach((key) => {
        initialDepartments[key] = true;
      });
      Object.keys(processedOptions).forEach((country) => {
        initialCountries[country] = true;
      });
      setCheckedDepartments(initialDepartments);
      setCheckedCountries(initialCountries);
      setIsSelectAllChecked(true);
    } else {
      const selectedArray = Array.isArray(value) ? value : [];

      Object.entries(processedOptions).forEach(([country, departments]) => {
        if (selectedArray.includes(country)) {
          // Country is selected - check all its departments
          initialCountries[country] = true;
          departments.forEach((dept) => {
            initialDepartments[dept.key] = true;
          });
        } else {
          // Check individual departments based on their titles
          let allDepartmentsChecked = true;
          departments.forEach((dept) => {
            // Check if any of the titles for this department are in selectedArray
            const isDepartmentSelected = dept.titles.some((title) => selectedArray.includes(title));
            initialDepartments[dept.key] = isDepartmentSelected;
            if (!isDepartmentSelected) {
              allDepartmentsChecked = false;
            }
          });
          initialCountries[country] = allDepartmentsChecked;
        }
      });

      setCheckedDepartments(initialDepartments);
      setCheckedCountries(initialCountries);
      setIsSelectAllChecked(false);
    }
  }, [value, processedOptions, allDepartmentKeys]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => {
    setSearch('');
    setAnchorEl(null);
  };

  const toggleGroup = (country: string) => {
    setExpandedGroups((prev) => ({ ...prev, [country]: !prev[country] }));
  };

  const handleDepartmentChange = (departmentKey: string, country: string, checked: boolean) => {
    setCheckedDepartments((prev) => ({
      ...prev,
      [departmentKey]: checked,
    }));

    // Update country checkbox - check if all departments in this country are checked
    const allDepartmentsInCountryChecked = processedOptions[country].every((dept) =>
      dept.key === departmentKey ? checked : checkedDepartments[dept.key]
    );

    setCheckedCountries((prev) => ({
      ...prev,
      [country]: allDepartmentsInCountryChecked,
    }));

    // Update "Select All"
    if (!checked) {
      setIsSelectAllChecked(false);
    } else {
      const allDepartmentsChecked = allDepartmentKeys.every((key) =>
        key === departmentKey ? checked : checkedDepartments[key]
      );
      setIsSelectAllChecked(allDepartmentsChecked);
    }
  };

  const handleCountryToggle = (country: string, checked: boolean) => {
    const updatedDepartments = { ...checkedDepartments };

    processedOptions[country].forEach((dept) => {
      updatedDepartments[dept.key] = checked;
    });

    setCheckedDepartments(updatedDepartments);
    setCheckedCountries((prev) => ({
      ...prev,
      [country]: checked,
    }));

    // Update "Select All"
    if (!checked) {
      setIsSelectAllChecked(false);
    } else {
      const allDepartmentsChecked = allDepartmentKeys.every((key) => {
        const belongsToToggledCountry = processedOptions[country].some((d) => d.key === key);
        return belongsToToggledCountry ? checked : updatedDepartments[key];
      });
      setIsSelectAllChecked(allDepartmentsChecked);
    }
  };

  const handleApply = () => {
    const selected: string[] = [];
    const displayItems: string[] = [];

    Object.entries(processedOptions).forEach(([country, departments]) => {
      const someDepartmentsChecked = departments.some((dept) => checkedDepartments[dept.key]);

      if (someDepartmentsChecked) {
        // Add titles for all checked departments in this country
        departments.forEach((dept) => {
          if (checkedDepartments[dept.key]) {
            selected.push(...dept.titles);
            displayItems.push(`${country} - ${dept.department}`);
          }
        });
        // // Show country name in display
        // selectedCountry.push(country);
      }
    });

    setDisplaySelectedCountry(displayItems.join(', '));

    const totalDepartmentCount = allDepartmentKeys.length;
    const checkedDepartmentCount = Object.values(checkedDepartments).filter(Boolean).length;

    if (checkedDepartmentCount === totalDepartmentCount && totalDepartmentCount > 0) {
      dispatch(selectFilterAction({ [apiKeyname]: ['ALL'] }));
      onChange('ALL');
    } else if (selected.length === 0) {
      dispatch(selectFilterAction({ [apiKeyname]: [] }));
      onChange([]);
      setDisplaySelectedCountry('');
    } else {
      dispatch(selectFilterAction({ [apiKeyname]: selected }));
      onChange(selected);
    }
    handleClose();
  };

  const handleClear = () => {
    setCheckedDepartments({});
    setCheckedCountries({});
    setIsSelectAllChecked(false);
    onChange([]);
    clearPerticularFilters(apiKeyname);
    dispatch(selectFilterRemoveAction({ [name]: 'ALL' }));
    setDisplaySelectedCountry('');
    handleClose();
  };

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  // Display value logic
  let displayValue = '';
  if (value === 'ALL') {
    displayValue = 'ALL';
  } else if (Array.isArray(value) && value.length > 0) {
    // Show department names or countries in display
    const displayItems: string[] = [];

    Object.entries(checkedCountries).forEach(([country, isChecked]) => {
      if (isChecked) {
        displayItems.push(country);
      }
    });

    Object.entries(processedOptions).forEach(([country, departments]) => {
      if (!checkedCountries[country]) {
        departments.forEach((dept) => {
          if (checkedDepartments[dept.key]) {
            // Only push if the country name isn't already in the list
            if (!displayItems.includes(country)) {
              displayItems.push(country);
            }
          }
        });
      }
    });

    // The currently loaded mockOptions (e.g. after switching metric pages) may not contain
    // an entry matching the stored selection yet, leaving displayItems empty even though a
    // selection exists. Fall back to the raw selected values so the field never appears
    // cleared while a selection is still applied.
    displayValue = displayItems.length > 0 ? displayItems.join(', ') : value.join(', ');
  }

  return (
    <Box sx={{ mb: 2 }}>
      <FormControl fullWidth>
        <Typography
          component="label"
          htmlFor={`collapsible-country-filter-${name}`}
          sx={{
            fontSize: '0.875rem',
            fontWeight: 500,
            color: 'primary.main',
            mb: 0.5,
            display: 'block',
          }}
        >
          {label}
        </Typography>

        <TextField
          id={`collapsible-country-filter-${name}`}
          value={displayValue}
          onClick={handleOpen}
          readOnly
          fullWidth
          size="small"
          placeholder="Select..."
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {displayValue && displayValue !== 'ALL' && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClear();
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
                <ArrowDropDownIcon sx={{ cursor: 'pointer', color: 'action.active' }} />
              </InputAdornment>
            ),
          }}
        />
      </FormControl>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: anchorEl ? anchorEl.clientWidth : 320,
            maxHeight: 450,
            mt: 0.5,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex',
            flexDirection: 'column',
          },
transition: {
            onEntered: () => searchInputRef.current?.focus(),
          },
        }}
      >
        {/* Search Box */}
        <Box sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            inputRef={searchInputRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f5f5f5',
              },
            }}
          />
        </Box>
        <Box>
          <MenuItem sx={{ py: 1, px: 2, borderBottom: '1px solid #e0e0e0' }} onClick={(e) => e.stopPropagation()}>
            <Checkbox
              size="small"
              checked={isSelectAllChecked}
              onChange={(e) => {
                const { checked } = e.target;
                const updatedDepartments: Record<string, boolean> = {};
                const updatedCountries: Record<string, boolean> = {};

                allDepartmentKeys.forEach((key) => {
                  updatedDepartments[key] = checked;
                });
                Object.keys(processedOptions).forEach((country) => {
                  updatedCountries[country] = checked;
                });

                setCheckedDepartments(updatedDepartments);
                setCheckedCountries(updatedCountries);
                setIsSelectAllChecked(checked);
              }}
              sx={{ p: 0.5, mr: 1 }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
              ALL
            </Typography>
          </MenuItem>
        </Box>
        {/* Scrollable Content Area */}
        <Box sx={{ overflowY: 'auto', flexGrow: 1, maxHeight: 250 }}>
          {/* ALL Checkbox */}

          {/* Country/Department List */}

          {Object.entries(processedOptions).map(([country, departments]) => {
            const searchText = search.toLowerCase();

            const filteredDepartments = departments.filter(
              (dept) =>
                dept.department.toLowerCase().includes(searchText) ||
                country.toLowerCase().includes(searchText) ||
                dept.titles.some((title) => title.toLowerCase().includes(searchText))
            );

            if (filteredDepartments.length === 0) return null;

            const isAllChecked = filteredDepartments.every((dept) => checkedDepartments[dept.key]);
            const isSomeChecked = filteredDepartments.some((dept) => checkedDepartments[dept.key]);
            const isExpanded = expandedGroups[country] || search.length > 0;

            return (
              <Box key={country}>
                {/* Country Header */}
                <Box
                  sx={{
                    py: 1.2,
                    px: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    minHeight: 50,
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                  onClick={() => toggleGroup(country)}
                >
                  <IconButton
                    size="small"
                    sx={{ p: 0, width: 24, height: 24 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGroup(country);
                    }}
                  >
                    {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                  </IconButton>
                  <Checkbox
                    size="small"
                    checked={isAllChecked}
                    indeterminate={isSomeChecked && !isAllChecked}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleCountryToggle(country, e.target.checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ p: 0.5 }}
                  />
                  <Typography variant="body2" sx={{ fontSize: '0.95rem' }}>
                    {country}
                  </Typography>
                </Box>

                {/* Department List */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ pl: 6, pr: 1.5, py: 0.5, backgroundColor: '#fafafa' }}>
                    {filteredDepartments.map((dept) => (
                      <Box key={dept.key} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                        <Checkbox
                          size="small"
                          checked={checkedDepartments[dept.key] || false}
                          onChange={(e) => handleDepartmentChange(dept.key, country, e.target.checked)}
                        />
                        <Typography variant="body2" sx={{ fontSize: '0.95rem' }}>
                          {dept.department}{' '}
                          {/* <Typography component="span" sx={{ fontSize: '0.75em', color: 'text.secondary' }}>
                              ({dept.titles.length} {dept.titles.length === 1 ? 'city' : 'cities'})
                            </Typography> */}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>

        {/* Action Buttons */}
        <Box
          onMouseDown={stop}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            p: 1,
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#fafafa',
            flexShrink: 0,
          }}
        >
          <Button size="small" onClick={handleClose} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleApply}
            sx={{ textTransform: 'none' }}
            disabled={Object.keys(mockOptions || {}).length === 0}
          >
            Apply
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};

export default CollapsibleCountryFilter;
